<template>
  <FlotoContentLoader
    classes="h-100"
    :transition="useTransition ? undefined : 'none'"
    :loading="loading"
  >
    <DetailView
      v-if="asset.id"
      default-sidebar-visible
      :disabled="isDisabled"
      :resource="asset"
      :is-approval="isApproval"
      :approval="approval"
      :additional-info-col-size="9"
      :module-name="moduleName"
      :update-api="update"
      :is-geolocation-supported="isGeolocationSupported"
      v-bind="{ ...$attrs }"
    >
      <template v-slot:before-title>
        <FlotoBackButton
          :to="
            backLink ||
            $modules.getModuleRoute('asset', '', {
              params: { assetType: moduleName },
            })
          "
          class="text-neutral-light mx-2"
        />
      </template>
      <template v-slot:actions>
        <slot name="actions">
          <BarcodeRenderer
            v-if="asset.barCode && moduleName !== $constants.CMDB"
            :key="asset.barCode"
            class="mx-2"
            :barcode="asset.barCode"
            :update-api="update"
            :module-name="moduleName"
            :asset="asset"
            :disabled="isDisabled"
            @refresh="refreshTicket"
          />
          <MTooltip
            v-if="moduleName === $constants.ASSET_SOFTWARE"
            placement="left"
          >
            <template v-slot:trigger>
              <div>
                <ComplinceNotificationBtn
                  :disabled="isDisabled"
                  :asset="asset"
                  @update="update"
                />
              </div>
            </template>
            {{ __t('installation_notification') }}
          </MTooltip>
          <QuantityNotificationBtn
            v-if="moduleName === $constants.ASSET_CONSUMABLE && !isDisabled"
            :disabled="isDisabled"
            :asset="asset"
            :update-api="update"
          />
          <PendingApprovalAlert
            v-if="(getApprovalStatusData || []).length"
            :asset="asset"
            :module-name="moduleName"
            :items="getApprovalStatusData"
          />
          <slot name="action-before" />
          <ActionMenu
            :asset="asset"
            :module-name="moduleName"
            :disabled="isDisabled"
            :update-api="update"
            :asset-config-info="assetConfigInfo"
            class="mx-2"
            @archive="handleArchiveTicket"
            @variance-detection="handleVarianceDetection"
            @update="update"
            @refresh="refreshTicket"
            @approval-changed="refreshTicket(['approval'])"
          />
          <slot name="action-after" />
        </slot>
      </template>
      <!-- default content as tabs -->
      <slot :ticket="asset">
        <div :key="tabsRerenderCount" class="tabs mt-6 flex flex-col h-100">
          <MAffix>
            <MTab v-model="activeTabKey" size="small" class="affixed-tab">
              <template v-for="tab in allowedTabs">
                <MTabPane v-if="!tab.disabled" :key="tab.id" :tab="tab.name" />
              </template>
            </MTab>
          </MAffix>
          <div class="my-4 flex flex-1">
            <component
              :is="tabComponent"
              :disabled="
                isDisabled ||
                (tabComponentProps.alwaysEditable
                  ? false
                  : tabComponentProps.disabled)
              "
              :resource-id="asset.id"
              :module-name="
                tabComponentProps.moduleName
                  ? tabComponentProps.moduleName
                  : moduleName
              "
              :resource="asset"
              v-bind="tabComponentProps"
              :update-api="update"
              @relation-added="handleRelationCount"
              @relation-removed="handleRelationRemoved"
              @refresh="refreshTicket"
              @refresh-page="refreshPage"
              @property-tab-data-blank="handlePropertyTabDataBlank"
            />
          </div>
        </div>
      </slot>
    </DetailView>
    <FlotoLoadError
      v-if="error"
      class="flex-1"
      :authorization-error="error.response && error.response.status === 403"
    />
  </FlotoContentLoader>
</template>

<script>
import { AssetTypeComputed } from '@state/modules/asset-type'
import { authComputed } from '@state/modules/auth'
import NoteContainer from '@components/notes/notes-container'
import RelationContainer from '@components/relation/relation-container'
import { getAssetTypeConfigInfoApi } from '@modules/asset-management/api/asset-type-api'
import ApprovalContainer from '@components/approval/approval-container'
import { getApprovalResourceDetailApi } from '@modules/my-approval/my-approval-api'
import { getRelationCountApi } from '@components/relation/relation-api'
import { findValuePathWithItems } from '@data/recursive'
import { PreferenceComputed } from '@state/modules/preference'
import { SoftwareTypeComputed } from '@state/modules/software-type'
import { CustomRulesComputed } from '@state/modules/custom-rules'
import {
  getApi,
  updateApi,
  deleteAssetApi,
  getUsedByOrLocationApi,
  varianceDetectionManuallyApi,
} from '../asset-api'
import ActionMenu from './action-menu'
import DetailView from './detail/detail.vue'
import PropertyContainer from './property/property-container'
import ComponentContainer from './property/component-container'
import ComponentTab from './property/component-tab'
import VirtualMachineTab from './property/virtual-machine-tab'
import AgentInformationTab from './property/agent-information'
import BarcodeRenderer from './barcode/barcode-renderer'
import ConsolidateSoftwareTab from './consolidate-software-tab'
import InstallationTab from './installation-tab'
import UninstallationTab from './uninstallation-tab'
import SoftwareLicenseTab from './software-license-tab'
import RDPRecordingTab from './rdp-recording-tab'
import ComplinceNotificationBtn from './complince-notification-btn'
import QuantityNotificationBtn from './quantity-notification-btn'
import {
  SOFTWARE_COMPONENT,
  USER_COMPONENT,
  MOBILE_APPLICATION_COMPONENT,
} from '../helpers/asset'
import {
  //  isAvailableRDP,
  getAssetTypeSystemName,
} from '../helpers/asset-type'
import { LicenseComputed } from '@state/modules/license'
import History from './history/history'
import SnmpCustomPorpertyTab from './snmp-custom-property/snmp-custom-property-tab'
import AssetRelationshipTab from './relationship/relationship.vue'
import FinancialTab from './financial/financial-tab'
import BaselineTab from './baseline/baseline-tab'
import SoftwareMeterTab from './software-meter-tab/software-meter-tab'
import ConsumablePropertyTab from './consumable-property-tab'
import AllocationContainer from '../components/allocation-container/allocation-list'
import GeoLocationTab from '../components/geo-location-tab.vue'
import PendingApprovalAlert from '../components/action-menu-items/pending-approval-alert'
import RelatedEntitiesContainer from './related-entities/related-entities-container'
import RelatedClustersContainer from './related-clusters/related-clusters-container'
// import { getGeolocationCoordinatesApi } from '@src/modules/asset/asset-api.js'

export default {
  name: 'AssetDetailView',
  components: {
    DetailView,
    ActionMenu,
    PropertyContainer,
    ComponentContainer,
    ComponentTab,
    VirtualMachineTab,
    BarcodeRenderer,
    NoteContainer,
    ComplinceNotificationBtn,
    QuantityNotificationBtn,
    ConsolidateSoftwareTab,
    InstallationTab,
    History,
    SnmpCustomPorpertyTab,
    SoftwareLicenseTab,
    AgentInformationTab,
    RDPRecordingTab,
    AssetRelationshipTab,
    FinancialTab,
    BaselineTab,
    ApprovalContainer,
    SoftwareMeterTab,
    UninstallationTab,
    ConsumablePropertyTab,
    GeoLocationTab,
    PendingApprovalAlert,
  },
  inheritAttrs: false,
  props: {
    moduleName: { type: String, required: true },
    useTransition: { type: Boolean, default: false },
    resourceId: { type: [String, Number], required: true },
    onChange: { type: Function, default: undefined },
    disabled: { type: Boolean, default: false },
    backLink: { type: Object, default: undefined },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
  },
  data() {
    return {
      asset: {},
      activeTabKey: 'property',
      tabsRerenderCount: 1,
      error: null,
      loading: true,
      allowedTabs: [],
      assetConfigInfo: {},
      propertyTabDataBlank: false,
      relationCount: 0,
      getApprovalStatusData: [],
    }
  },
  computed: {
    ...LicenseComputed,
    ...authComputed,
    ...AssetTypeComputed,
    ...PreferenceComputed,
    ...SoftwareTypeComputed,
    ...CustomRulesComputed,
    hasAssetModule() {
      return this.availableModulesInLicense.indexOf(this.$constants.ASSET) >= 0
    },
    hasCmdbModule() {
      return this.availableModulesInLicense.indexOf(this.$constants.CMDB) >= 0
    },
    isDisabled() {
      return this.disabled || this.asset.archived
    },
    tabComponent() {
      return (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
        .component
    },
    tabComponentProps() {
      return (
        (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
          .componentProps || {}
      )
    },
    isProhibited() {
      const prohibitedId = this.softwareTypeOptions.find(
        (l) => l.systemName === 'Prohibited'
      ).id
      if (prohibitedId === this.asset.swTypeId) {
        return true
      }
      return false
    },
    isGeolocationSupported() {
      if (
        this.moduleName !== this.$constants.CMDB &&
        this.assetConfigInfo.geoLocationSupported &&
        this.myAllowedModules.indexOf('asset.can_view_geo_location') >= 0
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    resourceId: {
      immediate: true,
      handler: 'getTicket',
    },
    'asset.assetTypeId': {
      immediate: true,
      handler(newValue, preValue) {
        if (newValue !== preValue && this.moduleName !== this.$constants.CMDB) {
          this.getAssetTypeConfigInfo(
            this.$constants.ASSET,
            'assettype',
            newValue
          ).then((info) => {
            this.setTabs(info)
          })
        }
      },
    },
    'asset.ciTypeId': {
      immediate: true,
      handler(newValue, preValue) {
        if (newValue !== preValue && this.moduleName === this.$constants.CMDB) {
          this.getAssetTypeConfigInfo(
            this.$constants.CMDB,
            'citype',
            newValue
          ).then((info) => {
            this.setTabs(info)
          })
        }
      },
    },
    'asset.swTypeId': {
      handler(newValue, preValue) {
        if (newValue !== preValue) {
          this.setTabs(this.assetConfigInfo)
        }
      },
    },
    allowedTabs: {
      handler(newValue, prevValue) {
        if (
          this.moduleName === this.$constants.ASSET_CONSUMABLE &&
          newValue !== prevValue
        ) {
          this.activeTabKey = 'consumable_property'
        }
      },
    },
  },
  created() {
    this.getRelationModuleCount()
    this.getUsedByOrLocation()
    // this.getGeolocationCoordinates()
  },
  methods: {
    getRelationModuleCount() {
      return getRelationCountApi(this.moduleName, this.resourceId).then(
        (data) => {
          Object.values(data).filter((value) => {
            this.relationCount = this.relationCount + value
          })
          this.setTabs(this.assetConfigInfo)
        }
      )
    },
    getUsedByOrLocation(limit, offset) {
      const filter = {
        type: 'used_by',
        ids: parseInt(this.resourceId),
        approvalStatus: 'pending',
      }
      if (
        this.moduleName === this.$constants.CMDB ||
        this.moduleName === this.$constants.ASSET_HARDWARE
      ) {
        filter.moduleNames = [
          ...(this.hasCmdbModule ? [this.$constants.CMDB] : []),
          ...(this.hasAssetModule ? [this.$constants.ASSET_HARDWARE] : []),
        ]
      } else {
        filter.moduleName = this.moduleName
      }
      return getUsedByOrLocationApi(filter, limit, offset).then((data) => {
        this.getApprovalStatusData = data.items.map((i) => i.value)
      })
    },
    handleRelationCount() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    handleRelationRemoved() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    getExcludedTabs(info) {
      const excluded = []
      // exclude property tab if property length not found
      // if (!info.property.length) {
      //   excluded.push('property')
      // }
      if (info && !info.vmwareServer) {
        excluded.push('virtual_machine')
      }
      if (info && !info.hyperVServer) {
        excluded.push('hyper_v_server')
      }
      if (this.propertyTabDataBlank) {
        excluded.push('property')
      }
      // exclude hardware tab if component length not found
      if (!(info.component || []).length) {
        excluded.push('hardware')
        excluded.push('software')
        excluded.push('user')
        excluded.push('application')
      }
      // exclude software tab if software component length not found
      if (
        (info.component || []).length &&
        info.component.indexOf(SOFTWARE_COMPONENT) === -1
      ) {
        excluded.push('software')
      }
      // exclude application tab if mobile application component length not found
      if (
        (info.component || []).length &&
        info.component.indexOf(MOBILE_APPLICATION_COMPONENT) === -1
      ) {
        excluded.push('application')
      }
      // exclude uset tab if uset component length not found
      if (
        (info.component || []).length &&
        info.component.indexOf(USER_COMPONENT) === -1
      ) {
        excluded.push('user')
      }
      // exclude uset tab if listOfRelatedEntities and clusters_tab length not found
      if (
        !info.listOfRelatedEntities ||
        info.listOfRelatedEntities.indexOf('clusters_tab') === -1
      ) {
        excluded.push('related_clusters')
      }
      // exclude uset tab if listOfRelatedEntities and clusters_tab length not found
      if (
        !info.listOfRelatedEntities ||
        (info.listOfRelatedEntities.indexOf('host_tab') === -1 &&
          info.listOfRelatedEntities.indexOf('storage_pool') === -1 &&
          info.listOfRelatedEntities.indexOf('virtual_machine') === -1 &&
          info.listOfRelatedEntities.indexOf('storage_container') === -1)
      ) {
        excluded.push('related_entities')
      }
      if (
        this.moduleName !== this.$constants.CMDB &&
        !(
          (this.asset.approvalStatus &&
            this.asset.approvalStatus !== 'pre_approved') ||
          (this.asset.checkerApprovalStatus &&
            this.asset.checkerApprovalStatus !== 'pre_approved')
        )
      ) {
        excluded.push('approval')
      }
      if (
        this.moduleName === this.$constants.CMDB &&
        !(
          this.asset.ciApprovalStatus &&
          this.asset.ciApprovalStatus !== 'pre_approved'
        )
      ) {
        excluded.push('approval')
      }
      // display consolidate software tab only for software assets
      if (
        [
          this.$constants.CMDB,
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
        ].indexOf(this.moduleName) >= 0
      ) {
        excluded.push('consolidate_software')
      }
      // display baseline tab only for hardware computer asset
      if (
        [
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
        ].indexOf(this.moduleName) >= 0 ||
        !info.isComputerAssetType
      ) {
        excluded.push('baseline')
      }
      if (
        [
          this.$constants.CMDB,
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
        ].indexOf(this.moduleName) >= 0
      ) {
        excluded.push('installation')
      }
      if (
        [
          this.$constants.CMDB,
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
        ].indexOf(this.moduleName) >= 0 ||
        !this.tenantPrefrences.enableAutoUnistalltionSoftware ||
        !this.isProhibited
      ) {
        excluded.push('uninstallation')
      }
      if (info.discoveryDeviceProtocol !== 'snmp') {
        excluded.push('snmp_custom_property')
      }
      if (
        this.asset &&
        (['asset_hardware', 'cmdb'].indexOf(this.moduleName) === -1 ||
          (['asset_hardware', 'cmdb'].indexOf(this.moduleName) >= 0 &&
            this.asset.origin !== 'agent' &&
            (!this.asset.agentActiveStatus ||
              (this.asset.agentActiveStatus &&
                this.asset.agentActiveStatus === 'not_applicable'))))
      ) {
        excluded.push('agent_information')
      }
      // if (this.asset.origin !== 'agent') {
      //   excluded.push('agent_information')
      // }
      if (this.asset && !this.asset.softwareLicenseId) {
        excluded.push('software_license')
      }
      if (!this.asset.rdpRecordingExists) {
        excluded.push('remote_desktop_recording')
      }
      if (
        [this.$constants.ASSET_SOFTWARE, this.$constants.CMDB].indexOf(
          this.moduleName
        ) >= 0
      ) {
        excluded.push('financial')
      }
      if (this.moduleName !== this.$constants.ASSET_SOFTWARE) {
        excluded.push('software_meter')
      }
      if (this.moduleName === this.$constants.ASSET_CONSUMABLE) {
        excluded.push('property')
        excluded.push('relationship')
        excluded.push('linked_events')
      }
      if (this.moduleName !== this.$constants.ASSET_CONSUMABLE) {
        excluded.push('consumable_property')
        excluded.push('allocation')
      }
      if (!this.isGeolocationSupported) {
        excluded.push('geolocation')
      }
      return excluded
    },
    setTabs(info) {
      const excluded = this.getExcludedTabs(info)
      let tabs = [
        {
          id: 'consumable_property',
          name: this.$tc('property', 2),
          disabled: excluded && excluded.indexOf('consumable_property') >= 0,
          component: ConsumablePropertyTab,
          componentProps: {
            resourceId: this.asset.id,
            asset: this.asset,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'allocation',
          name: this.$tc('allocation', 2),
          disabled: excluded && excluded.indexOf('allocation') >= 0,
          component: AllocationContainer,
          componentProps: {
            resourceId: this.asset.id,
            asset: this.asset,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'property',
          disabled: excluded && excluded.indexOf('property') >= 0,
          name: this.$tc('property', 2),
          component: PropertyContainer,
          componentProps: {
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'hardware',
          name: this.$tc('hardware', 2),
          disabled: excluded && excluded.indexOf('hardware') >= 0,
          component: ComponentContainer,
          componentProps: {
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'baseline',
          name: `${this.$tc('baseline')}`,
          disabled: excluded && excluded.indexOf('baseline') >= 0,
          component: BaselineTab,
        },
        {
          id: 'software',
          name: this.$tc('software', 2),
          disabled: excluded && excluded.indexOf('software') >= 0,
          component: ComponentTab,
          componentProps: {
            componentType: SOFTWARE_COMPONENT,
            isCreateDisabled: !(
              (!this.availableModules ||
                this.availableModules.indexOf(this.$constants.ASSET) >= 0) &&
              this.availableModulesInLicense.indexOf(this.$constants.ASSET) >= 0
            ),
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'application',
          name: this.$tc('application', 2),
          disabled: excluded && excluded.indexOf('application') >= 0,
          component: ComponentTab,
          componentProps: {
            isCreateDisabled: !(
              (!this.availableModules ||
                this.availableModules.indexOf(this.$constants.ASSET) >= 0) &&
              this.availableModulesInLicense.indexOf(this.$constants.ASSET) >= 0
            ),
            componentType: MOBILE_APPLICATION_COMPONENT,
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'virtual_machine',
          name: this.$tc('virtual_machine', 2),
          disabled: excluded && excluded.indexOf('virtual_machine') >= 0,
          component: VirtualMachineTab,
        },
        {
          id: 'related_clusters',
          name: this.$tc('related_clusters'),
          disabled: excluded && excluded.indexOf('related_clusters') >= 0,
          component: RelatedClustersContainer,
          componentProps: {
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'related_entities',
          name: this.$tc('related_entities'),
          disabled: excluded && excluded.indexOf('related_entities') >= 0,
          component: RelatedEntitiesContainer,
          componentProps: {
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'hyper_v_server',
          name: this.$tc('virtual_machine', 2),
          disabled: excluded && excluded.indexOf('hyper_v_server') >= 0,
          component: VirtualMachineTab,
        },
        {
          id: 'user',
          name: this.$tc('user', 2),
          disabled: excluded && excluded.indexOf('user') >= 0,
          component: ComponentTab,
          componentProps: {
            componentType: USER_COMPONENT,
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'approval',
          name: this.$tc('approval', 2),
          disabled: excluded && excluded.indexOf('approval') >= 0,
          component: ApprovalContainer,
        },
        {
          id: 'consolidate_software',
          name: `${this.$tc('consolidated')} ${this.$tc('software')}`,
          disabled: excluded && excluded.indexOf('consolidate_software') >= 0,
          component: ConsolidateSoftwareTab,
        },
        {
          id: 'installation',
          name: `${this.$tc('installation')}`,
          disabled: excluded && excluded.indexOf('installation') >= 0,
          component: InstallationTab,
        },
        {
          id: 'uninstallation',
          name: `${this.$tc('uninstallation')}`,
          disabled: excluded && excluded.indexOf('uninstallation') >= 0,
          component: UninstallationTab,
        },
        {
          id: 'software_meter',
          name: `${this.$tc('meter')}`,
          disabled: excluded && excluded.indexOf('software_meter') >= 0,
          component: SoftwareMeterTab,
        },
        {
          id: 'snmp_custom_property',
          name: `${this.$tc('snmp_custom_property')}`,
          disabled: excluded && excluded.indexOf('snmp_custom_property') >= 0,
          component: SnmpCustomPorpertyTab,
        },
        {
          id: 'relationship',
          name: this.__tc('relationship', 2),
          disabled: excluded && excluded.indexOf('relationship') >= 0,
          component: AssetRelationshipTab,
          componentProps: {
            moduleName:
              this.moduleName === this.$constants.CMDB
                ? this.$constants.CMDB
                : this.$constants.ASSET,
            subModuleName: this.moduleName,
          },
        },
        {
          id: 'linked_events',
          name: `${this.__tc('linked_event', 2)} (${this.relationCount})`,
          disabled: excluded && excluded.indexOf('linked_events') >= 0,
          component: RelationContainer,
          componentProps: {
            moduleName:
              this.moduleName === this.$constants.CMDB
                ? this.$constants.CMDB
                : this.$constants.ASSET,
            subModuleName: this.moduleName,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'history',
          name: this.$tc('history'),
          component: History,
          componentProps: {
            assetConfigInfo: info,
          },
        },
        {
          id: 'note',
          name: this.$tc('note', 2),
          component: NoteContainer,
          componentProps: {
            alwaysEditable: true,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'agent_information',
          name: this.$tc('agent_information'),
          disabled: excluded && excluded.indexOf('agent_information') >= 0,
          component: AgentInformationTab,
          componentProps: {
            resourceId: this.asset.id,
            asset: this.asset,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'software_license',
          name: this.$tc('license'),
          disabled: excluded && excluded.indexOf('software_license') >= 0,
          component: SoftwareLicenseTab,
        },
        {
          id: 'remote_desktop_recording',
          name: `${this.$tc('remote_desktop')} ${this.$tc('recording', 2)}`,
          disabled:
            excluded && excluded.indexOf('remote_desktop_recording') >= 0,
          component: RDPRecordingTab,
        },
        {
          id: 'financial',
          name: `${this.$tc('financial', 2)}`,
          disabled: excluded && excluded.indexOf('financial') >= 0,
          component: FinancialTab,
        },
        {
          id: 'geolocation',
          name: `${this.$tc('geolocation', 2)}`,
          disabled: excluded && excluded.indexOf('geolocation') >= 0,
          component: GeoLocationTab,
          componentProps: {
            assetConfigInfo: info,
            stickySearch: true,
            offsetTop: 38,
          },
        },
      ]
      this.allowedTabs = Object.freeze(tabs)
    },
    handlePropertyTabDataBlank(isDataBlank) {
      this.$nextTick(() => {
        this.propertyTabDataBlank = isDataBlank
        this.setTabs(this.assetConfigInfo)
        if (isDataBlank) {
          setTimeout(() => {
            this.activeTabKey = 'linked_events'
          })
        }
      })
    },
    getAssetTypeConfigInfo(moduleName, type, assetTypeId) {
      return getAssetTypeConfigInfoApi(moduleName, type, assetTypeId).then(
        (data) => {
          const d = {
            ...data,
          }
          if (this.moduleName !== this.$constants.CMDB) {
            const assetTypePath = findValuePathWithItems(
              this.assetTypes,
              assetTypeId
            )
            const systemNamePath = assetTypePath
              .map((a) => a.systemName)
              .filter((e) => e)
            const computerAssetTypeSystemName = getAssetTypeSystemName.computer
            d.isComputerAssetType =
              systemNamePath.indexOf(computerAssetTypeSystemName) >= 0
          }
          this.assetConfigInfo = d
          return d
        }
      )
    },
    getTicket() {
      this.loading = true
      this.asset = {}
      const api = this.isApproval ? getApprovalResourceDetailApi : getApi
      return api(
        this.isApproval && this.moduleName === this.$constants.CMDB
          ? this.approval.parentResource
          : this.moduleName,
        this.resourceId,
        this.isApproval ? this.isPortalLogin : { archived: true }
      )
        .then((asset) => {
          this.loading = false
          this.asset = asset
          this.$emit('resource', asset)
          return asset
        })
        .catch((e) => {
          this.error = e
          this.loading = false
          this.$router.replace(
            this.moduleName === this.$constants.CMDB
              ? this.$currentModule.getRoute('', {
                  params: { ciType: this.$route.params.ciType },
                })
              : this.$currentModule.getRoute('', {
                  params: { assetType: this.$route.params.assetType },
                })
          )
        })
    },
    refreshPage() {
      this.activeTabKey = 'property'
      this.refreshTicket().then(() => {
        this.$nextTick(() => {
          this.setTabs(this.assetConfigInfo)
        })
      })
    },
    refreshTicket(reRenderTabs = undefined) {
      return getApi(this.moduleName, this.resourceId, { archived: true }).then(
        (asset) => {
          this.asset = asset
          if (reRenderTabs && reRenderTabs.indexOf(this.activeTabKey) >= 0) {
            this.tabsRerenderCount = this.tabsRerenderCount + 1
          }
          if (
            reRenderTabs &&
            reRenderTabs.indexOf('approval') >= 0 &&
            this.allowedTabs.find((t) => t.id === 'approval') &&
            this.activeTabKey !== 'approval' &&
            asset.approvalStatus !== 'pre_approved'
          ) {
            this.activeTabKey = 'approval'
            this.$nextTick(() => {
              this.setTabs(this.assetConfigInfo)
            })
          }
          if (
            reRenderTabs &&
            reRenderTabs.indexOf('financial') >= 0 &&
            this.allowedTabs.find((t) => t.id === 'financial') &&
            this.activeTabKey === 'financial'
          ) {
            this.activeTabKey = 'financial'
            this.$nextTick(() => {
              this.setTabs(this.assetConfigInfo)
            })
          }
          if (
            reRenderTabs &&
            reRenderTabs.indexOf('property') >= 0 &&
            this.allowedTabs.find((t) => t.id === 'property') &&
            this.activeTabKey !== 'property'
          ) {
            this.activeTabKey = 'property'
          }
          if (
            reRenderTabs &&
            reRenderTabs.indexOf('geolocation') >= 0 &&
            this.allowedTabs.find((t) => t.id === 'geolocation') &&
            this.activeTabKey === 'geolocation'
          ) {
            this.activeTabKey = 'geolocation'
            this.$nextTick(() => {
              this.setTabs(this.assetConfigInfo)
            })
          }
          return asset
        }
      )
    },
    update(change, forceUpdate = false) {
      if (Object.keys(change).length) {
        const message = this[`${this.moduleName}AskForApproval`]
          ? this.$tc('your_request_is_submitted_for_approval')
          : undefined
        return updateApi(
          this.moduleName,
          {
            id: this.asset.id,
            type: this.asset.type,
            ...change,
          },
          this.moduleName === this.$constants.CMDB
            ? this.$constants.CI
            : undefined,
          undefined,
          message
        ).then((data) => {
          this.asset = { ...this.asset, ...data }
          if (this.onChange) {
            this.onChange(data)
          }
          if ('productId' in change || 'acquitionTime' in change) {
            this.refreshTicket(['financial'])
          }
          if ('assetTypeId' in change) {
            return this.moduleName === this.$constants.ASSET_CONSUMABLE
              ? this.refreshTicket(['consumable_property'])
              : this.refreshTicket(['property'])
          }
          if ('ciTypeId' in change) {
            this.refreshTicket(['property'])
          }
          if ('assignedUserIds' in change) {
            this.getUsedByOrLocation()
          }
          if (
            this[`${this.moduleName}AskForApproval`] &&
            data.checkerApprovalStatus &&
            data.checkerApprovalStatus !== 'pre_approved'
          ) {
            return this.refreshTicket(['approval'])
          }
          // if (this.activeTabKey === 'geolocation') {
          //   this.refreshTicket(['geolocation'])
          // }
          return data
        })
      }
      return this.moduleName === this.$constants.ASSET_CONSUMABLE
        ? this.refreshTicket(['consumable_property'])
        : this.refreshTicket(['property'])
    },
    handleArchiveTicket(archiveAgent) {
      deleteAssetApi(this.moduleName, this.asset.id, {
        archiveAgent: archiveAgent,
      }).then(() => this.$emit('delete'))
    },
    handleVarianceDetection() {
      varianceDetectionManuallyApi(this.asset.id).then((data) => {
        return this.refreshTicket(['baseline'])
      })
    },
    // @TODO 8.3 TFS-1912
    // getGeolocationCoordinates() {
    //   let filters = {}
    //   if (this.asset.id) {
    //     filters.ids = this.asset.id
    //   }
    //   return getGeolocationCoordinatesApi(filters).then((data) => {
    //     const locationName = data.items[0]
    //     this.asset = {
    //       ...this.asset,
    //       geolocationName: locationName.locationName,
    //     }
    //   })
    // },
  },
}
</script>
